import md5Lib from "blueimp-md5";
/**
 * Lightweight MD5 implementation for string inputs (UTF-8).
 * Returns lowercase hex digest.
 * Source adapted and typed for this codebase.
 */
export const md5 = (input: string): string => {
  // UTF-8 encode
  const str = encodeURIComponent(input).replace(/%([0-9A-F]{2})/g, (_m, p1) =>
    String.fromCharCode(parseInt(p1, 16))
  );

  const rotateLeft = (value: number, shift: number): number =>
    (value << shift) | (value >>> (32 - shift));

  const addUnsigned = (x: number, y: number): number => {
    const x4 = x & 0x40000000;
    const y4 = y & 0x40000000;
    const x8 = x & 0x80000000;
    const y8 = y & 0x80000000;
    const result = (x & 0x3fffffff) + (y & 0x3fffffff);
    if (x4 & y4) return result ^ 0x80000000 ^ x8 ^ y8;
    if (x4 | y4) {
      if (result & 0x40000000) return result ^ 0xc0000000 ^ x8 ^ y8;
      return result ^ 0x40000000 ^ x8 ^ y8;
    }
    return result ^ x8 ^ y8;
  };

  const F = (x: number, y: number, z: number) => (x & y) | (~x & z);
  const G = (x: number, y: number, z: number) => (x & z) | (y & ~z);
  const H = (x: number, y: number, z: number) => x ^ y ^ z;
  const I = (x: number, y: number, z: number) => y ^ (x | ~z);

  const FF = (
    a: number,
    b: number,
    c: number,
    d: number,
    x: number,
    s: number,
    ac: number
  ) =>
    addUnsigned(
      rotateLeft(
        addUnsigned(addUnsigned(a, F(b, c, d)), addUnsigned(x, ac)),
        s
      ),
      b
    );

  const GG = (
    a: number,
    b: number,
    c: number,
    d: number,
    x: number,
    s: number,
    ac: number
  ) =>
    addUnsigned(
      rotateLeft(
        addUnsigned(addUnsigned(a, G(b, c, d)), addUnsigned(x, ac)),
        s
      ),
      b
    );

  const HH = (
    a: number,
    b: number,
    c: number,
    d: number,
    x: number,
    s: number,
    ac: number
  ) =>
    addUnsigned(
      rotateLeft(
        addUnsigned(addUnsigned(a, H(b, c, d)), addUnsigned(x, ac)),
        s
      ),
      b
    );

  const II = (
    a: number,
    b: number,
    c: number,
    d: number,
    x: number,
    s: number,
    ac: number
  ) =>
    addUnsigned(
      rotateLeft(
        addUnsigned(addUnsigned(a, I(b, c, d)), addUnsigned(x, ac)),
        s
      ),
      b
    );

  const convertToWordArray = (s: string): number[] => {
    const lMessageLength = s.length;
    const lNumberOfWords_temp1 = lMessageLength + 8;
    const lNumberOfWords_temp2 =
      (lNumberOfWords_temp1 - (lNumberOfWords_temp1 % 64)) / 64;
    const lNumberOfWords = (lNumberOfWords_temp2 + 1) * 16;
    const lWordArray = new Array<number>(lNumberOfWords).fill(0);
    let lByteCount = 0;
    while (lByteCount < lMessageLength) {
      const lWordCountIndex = (lByteCount - (lByteCount % 4)) / 4;
      const lBytePosition = (lByteCount % 4) * 8;
      const prev = lWordArray[lWordCountIndex] ?? 0;
      lWordArray[lWordCountIndex] =
        prev | (s.charCodeAt(lByteCount) << lBytePosition);
      lByteCount++;
    }
    const lWordCountIndex = (lByteCount - (lByteCount % 4)) / 4;
    const lBytePosition = (lByteCount % 4) * 8;
    lWordArray[lWordCountIndex] =
      (lWordArray[lWordCountIndex] ?? 0) | (0x80 << lBytePosition);
    lWordArray[lNumberOfWords - 2] = lMessageLength << 3;
    lWordArray[lNumberOfWords - 1] = lMessageLength >>> 29;
    return lWordArray;
  };

  const wordToHex = (value: number): string => {
    let out = "";
    for (let i = 0; i <= 3; i++) {
      const byte = (value >>> (i * 8)) & 255;
      const tmp = "0" + byte.toString(16);
      out += tmp.substring(tmp.length - 2);
    }
    return out;
  };

  const x: number[] = convertToWordArray(str);
  let a = 0x67452301;
  let b = 0xefcdab89;
  let c = 0x98badcfe;
  let d = 0x10325476;

  const xLen: number = x.length;
  const get = (i: number): number =>
    i >= 0 && i < xLen ? (x[i] as number) : 0;

  for (let k = 0; k < xLen; k += 16) {
    const AA = a;
    const BB = b;
    const CC = c;
    const DD = d;

    a = FF(a, b, c, d, get(k + 0), 7, 0xd76aa478);
    d = FF(d, a, b, c, get(k + 1), 12, 0xe8c7b756);
    c = FF(c, d, a, b, get(k + 2), 17, 0x242070db);
    b = FF(b, c, d, a, get(k + 3), 22, 0xc1bdceee);
    a = FF(a, b, c, d, get(k + 4), 7, 0xf57c0faf);
    d = FF(d, a, b, c, get(k + 5), 12, 0x4787c62a);
    c = FF(c, d, a, b, get(k + 6), 17, 0xa8304613);
    b = FF(b, c, d, a, get(k + 7), 22, 0xfd469501);
    a = FF(a, b, c, d, get(k + 8), 7, 0x698098d8);
    d = FF(d, a, b, c, get(k + 9), 12, 0x8b44f7af);
    c = FF(c, d, a, b, get(k + 10), 17, 0xffff5bb1);
    b = FF(b, c, d, a, get(k + 11), 22, 0x895cd7be);
    a = FF(a, b, c, d, get(k + 12), 7, 0x6b901122);
    d = FF(d, a, b, c, get(k + 13), 12, 0xfd987193);
    c = FF(c, d, a, b, get(k + 14), 17, 0xa679438e);
    b = FF(b, c, d, a, get(k + 15), 22, 0x49b40821);

    a = GG(a, b, c, d, get(k + 1), 5, 0xf61e2562);
    d = GG(d, a, b, c, get(k + 6), 9, 0xc040b340);
    c = GG(c, d, a, b, get(k + 11), 14, 0x265e5a51);
    b = GG(b, c, d, a, get(k + 0), 20, 0xe9b6c7aa);
    a = GG(a, b, c, d, get(k + 5), 5, 0xd62f105d);
    d = GG(d, a, b, c, get(k + 10), 9, 0x02441453);
    c = GG(c, d, a, b, get(k + 15), 14, 0xd8a1e681);
    b = GG(b, c, d, a, get(k + 4), 20, 0xe7d3fbc8);
    a = GG(a, b, c, d, get(k + 9), 5, 0x21e1cde6);
    d = GG(d, a, b, c, get(k + 14), 9, 0xc33707d6);
    c = GG(c, d, a, b, get(k + 3), 14, 0xf4d50d87);
    b = GG(b, c, d, a, get(k + 8), 20, 0x455a14ed);

    a = HH(a, b, c, d, get(k + 5), 4, 0xa9e3e905);
    d = HH(d, a, b, c, get(k + 8), 11, 0xfcefa3f8);
    c = HH(c, d, a, b, get(k + 11), 16, 0x676f02d9);
    b = HH(b, c, d, a, get(k + 14), 23, 0x8d2a4c8a);
    a = HH(a, b, c, d, get(k + 1), 4, 0xfffa3942);
    d = HH(d, a, b, c, get(k + 4), 11, 0x8771f681);
    c = HH(c, d, a, b, get(k + 7), 16, 0x6d9d6122);
    b = HH(b, c, d, a, get(k + 10), 23, 0xfde5380c);
    a = HH(a, b, c, d, get(k + 13), 4, 0xa4beea44);
    d = HH(d, a, b, c, get(k + 0), 11, 0x4bdecfa9);
    c = HH(c, d, a, b, get(k + 3), 16, 0xf6bb4b60);
    b = HH(b, c, d, a, get(k + 6), 23, 0xbebfbc70);

    a = II(a, b, c, d, get(k + 0), 6, 0xf4292244);
    d = II(d, a, b, c, get(k + 7), 10, 0x432aff97);
    c = II(c, d, a, b, get(k + 14), 15, 0xab9423a7);
    b = II(b, c, d, a, get(k + 5), 21, 0xfc93a039);
    a = II(a, b, c, d, get(k + 12), 6, 0x655b59c3);
    d = II(d, a, b, c, get(k + 3), 10, 0x8f0ccc92);
    c = II(c, d, a, b, get(k + 10), 15, 0xffeff47d);
    b = II(b, c, d, a, get(k + 1), 21, 0x85845dd1);

    a = addUnsigned(a, AA);
    b = addUnsigned(b, BB);
    c = addUnsigned(c, CC);
    d = addUnsigned(d, DD);
  }

  return (
    wordToHex(a) +
    wordToHex(b) +
    wordToHex(c) +
    wordToHex(d)
  ).toLowerCase();
};

// Gravatar helper
// Note: defaultType is set to "404" so missing gravatars return HTTP 404.
// This lets the caller fall back to initials instead of a placeholder image.
// If you prefer a placeholder from Gravatar (e.g. "mp"), change the default below.
export const gravatarUrl = (
  email: string,
  size: number = 80,
  defaultType: string = "404"
): string => {
  const hash = md5Lib(email.trim().toLowerCase());
  const s = Math.max(16, Math.min(512, size));
  const d = encodeURIComponent(defaultType);
  return `https://www.gravatar.com/avatar/${hash}?d=${d}&s=${s}`;
};
